import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import FormProgressBar from './FormProgressBar';
import SaveForLaterModal from './SaveForLaterModal';
import { useParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { formIntakeApi, FormIntakeSectionDef, FormIntakeFieldDef, FormIntakeSchemaDef, FormIntakeStyleDef } from '../../services/api';
import './FormIntake.css';
import '../Booking/Booking.css';

// ─── helpers ────────────────────────────────────────────────────────────────

const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  return fallback;
};

// A value counts as "filled" for progress purposes. Note: `false` from an unchecked single
// checkbox counts as NOT filled; `0` counts as filled.
const isFilled = (v: any): boolean =>
  v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);

// Merge a saved draft onto the schema-derived skeleton — MERGE, never replace. The skeleton
// (built fresh from the current schema) is the source of truth for which keys should exist; a
// draft saved before a section was added/removed leaves an orphaned or missing key, and this
// discards orphans / keeps the skeleton's default for anything the draft doesn't have — so a
// stale draft against an edited schema degrades safely instead of crashing formData consumers.
const mergeDraft = (
  skeleton: Record<string, any>,
  draft: Record<string, any> | null | undefined
): Record<string, any> => {
  if (!draft) return skeleton;
  const merged = { ...skeleton };
  for (const key of Object.keys(skeleton)) {
    if (Object.prototype.hasOwnProperty.call(draft, key)) {
      merged[key] = draft[key];
    }
  }
  return merged;
};

const relativeTime = (iso: string): string => {
  const then = new Date(iso).getTime();
  const diffMin = Math.round((Date.now() - then) / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.round(diffHr / 24);
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
};

const LOCAL_DRAFT_KEY = (token: string) => `aime_pff_draft_${token}`;
// Maps an anonymous/open token to the invite token it was promoted to (Phase 5 save-for-later),
// so a visitor who revisits the ORIGINAL open link later still lands back on their saved draft.
const RESUME_MAP_KEY = (token: string) => `aime_pff_resume_${token}`;

const buildSkeleton = (schema: FormIntakeSchemaDef): Record<string, any> => {
  const initial: Record<string, any> = {};
  schema.sections.forEach((section) => {
    initial[section.key] = section.repeating ? [{}] : {};
  });
  return initial;
};

// The bar never reads a literal 0% on open — opening the form is itself "progress" (agreed
// design: a client shouldn't feel like they're starting from nothing on a 26-section form).
const PROGRESS_FLOOR = 8;

// Repeating sections always start a fresh page regardless of manual page_break_before placement
// — a repeating group split across a page boundary ("Additional Family Member #2, #3…"
// continuing overleaf) reads badly. Module-level constant so it can be flipped in one place.
const REPEATING_STARTS_PAGE = true;

// ─── Field renderer ──────────────────────────────────────────────────────────

interface FieldProps {
  field: FormIntakeFieldDef;
  value: any;
  onChange: (v: any) => void;
}

const FieldInput: React.FC<FieldProps> = ({ field, value, onChange }) => {
  const id = `field-${field.id}`;

  const label = (
    <label className="pff-label" htmlFor={id}>
      {field.label}
      {field.required && <span className="pff-required">*</span>}
    </label>
  );

  const helpEl = field.help ? <p className="fi-help">{field.help}</p> : null;

  switch (field.type) {
    case 'textarea':
      return (
        <div className="pff-field">
          {label}
          {helpEl}
          <textarea
            id={id}
            className="pff-textarea"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
          />
        </div>
      );

    case 'select':
      return (
        <div className="pff-field">
          {label}
          {helpEl}
          <select
            id={id}
            className="pff-select"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
          >
            <option value="">Select...</option>
            {(field.options ?? []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      );

    case 'checkbox': {
      // If options are provided, render multi-checkbox; otherwise single checkbox
      if (field.options && field.options.length > 0) {
        const checked: string[] = Array.isArray(value) ? value : [];
        return (
          <div className="pff-field">
            {label}
            {helpEl}
            {field.options.map((opt) => (
              <label key={opt} className="pff-checkbox-row">
                <input
                  type="checkbox"
                  checked={checked.includes(opt)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange([...checked, opt]);
                    } else {
                      onChange(checked.filter((v) => v !== opt));
                    }
                  }}
                />
                {opt}
              </label>
            ))}
          </div>
        );
      }
      return (
        <div className="pff-field">
          <label className="pff-checkbox-row">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
            />
            <span className="pff-label">
              {field.label}
              {field.required && <span className="pff-required">*</span>}
            </span>
          </label>
          {helpEl}
        </div>
      );
    }

    case 'radio': {
      const options = field.options ?? [];
      return (
        <div className="pff-field">
          {label}
          {helpEl}
          {options.map((opt) => (
            <label key={opt} className="pff-radio-row">
              <input
                type="radio"
                name={id}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(opt)}
                required={field.required}
              />
              {opt}
            </label>
          ))}
        </div>
      );
    }

    default:
      return (
        <div className="pff-field">
          {label}
          {helpEl}
          <input
            id={id}
            className="pff-input"
            type={field.type}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            required={field.required}
          />
        </div>
      );
  }
};

// ─── RepeatingSection ────────────────────────────────────────────────────────

interface RepeatingSectionProps {
  section: FormIntakeSectionDef;
  items: Record<string, any>[];
  onChange: (items: Record<string, any>[]) => void;
}

const RepeatingSection: React.FC<RepeatingSectionProps> = ({ section, items, onChange }) => {
  const addItem = () => onChange([...items, {}]);
  const removeItem = (idx: number) => onChange(items.filter((_, i) => i !== idx));

  const updateField = (itemIdx: number, fieldId: string, val: any) => {
    const next = items.map((item, i) =>
      i === itemIdx ? { ...item, [fieldId]: val } : item
    );
    onChange(next);
  };

  return (
    <div className="pff-section">
      <h3 className="pff-section-title">{section.title}</h3>
      {section.description && <p className="fi-help">{section.description}</p>}
      {items.map((item, itemIdx) => (
        <div key={itemIdx} className="pff-repeating-item">
          <div className="pff-repeating-item-header">
            <span>
              {section.item_label ?? 'Item'} {itemIdx + 1}
            </span>
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => removeItem(itemIdx)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--danger)',
                  fontSize: 'var(--text-sm)',
                  padding: '2px 6px',
                }}
              >
                Remove
              </button>
            )}
          </div>
          {section.fields.map((field) => (
            <FieldInput
              key={field.id}
              field={field}
              value={item[field.id]}
              onChange={(v) => updateField(itemIdx, field.id, v)}
            />
          ))}
        </div>
      ))}
      <button
        type="button"
        onClick={addItem}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: 'var(--text-sm)',
          color: 'var(--primary)',
          background: 'none',
          border: '1px dashed var(--stroke)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-2) var(--space-3)',
          cursor: 'pointer',
          width: '100%',
          justifyContent: 'center',
          marginTop: 'var(--space-2)',
        }}
      >
        + Add another {section.item_label ?? 'item'}
      </button>
    </div>
  );
};

// ─── PublicFormFill ──────────────────────────────────────────────────────────

type PageState = 'loading' | 'error' | 'pin' | 'form' | 'submitted' | 'already_submitted';

const PublicFormFill: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [formName, setFormName] = useState('');
  const [schema, setSchema] = useState<FormIntakeSchemaDef | null>(null);
  const [styleDef, setStyleDef] = useState<FormIntakeStyleDef | null>(null);
  const [requiresPin, setRequiresPin] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // Booking CTA state
  const [bookingTypeId, setBookingTypeId] = useState<string | null>(null);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  // Pagination — declared here (not just before use) so load() can reset it on a fresh fetch.
  const [pageIdx, setPageIdx] = useState(0);
  // Partial save/resume (Phase 4)
  const [isOpenToken, setIsOpenToken] = useState(false);
  const [resumeBanner, setResumeBanner] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'local' | 'error' | 'expired'>('idle');
  // Anonymous save-for-later → invite promotion (Phase 5). `resumeToken` overrides the URL's
  // `token` param once a save-for-later email mints a fresh invite mid-session, WITHOUT a route
  // navigation (window.history.replaceState only) — every save/submit call below must use
  // `activeToken`, never the raw `token` from useParams, once this is set.
  const [resumeToken, setResumeToken] = useState<string | null>(null);
  const activeToken = resumeToken ?? token;
  const [showSaveForLaterModal, setShowSaveForLaterModal] = useState(false);

  const load = useCallback(async () => {
    if (!token) {
      setErrorMsg('This link is invalid or has expired.');
      setPageState('error');
      return;
    }
    try {
      const res = await formIntakeApi.publicGetForm(token);
      if (res.already_submitted) {
        setPageState('already_submitted');
        return;
      }
      setFormName(res.name);
      setSchema(res.schema);
      setStyleDef(res.style ?? null);
      setRequiresPin(res.requires_pin);
      setIsOpenToken(res.open);
      setResumeBanner(null);

      // Initialise form data — the schema-derived skeleton is always built first; any draft
      // found below is MERGED onto it (mergeDraft), never used to replace it wholesale.
      const initial = buildSkeleton(res.schema);

      if (res.open) {
        // A prior visit to this exact open link may have promoted to an invite token (Phase 5
        // save-for-later). If so, send the visitor straight to their saved-progress link instead
        // of starting fresh — a full navigation (not history.replaceState) since this is a
        // brand-new page load, not the live post-promotion session. Verify the mapped token is
        // still usable FIRST: once it's been submitted or has expired, blindly redirecting would
        // permanently strand every future visit to this open/campaign link on the same device —
        // clear the stale mapping instead and fall through to the normal open-token flow below.
        const mappedToken = localStorage.getItem(RESUME_MAP_KEY(token));
        if (mappedToken && mappedToken !== token) {
          try {
            const mappedRes = await formIntakeApi.publicGetForm(mappedToken);
            if (!mappedRes.already_submitted) {
              window.location.replace(`/form-fill/${mappedToken}`);
              return;
            }
          } catch {
            // 404/410 — the invite is gone or expired. Either way it's dead; stop pointing at it.
          }
          try {
            localStorage.removeItem(RESUME_MAP_KEY(token));
          } catch {
            // Non-fatal — worst case we retry this same check next visit.
          }
        }
        // Anonymous/open link: same-device safety net only. No server round-trip, no identity.
        try {
          const raw = localStorage.getItem(LOCAL_DRAFT_KEY(token));
          if (raw) {
            const parsed = JSON.parse(raw);
            setFormData(mergeDraft(initial, parsed.data));
            setPageIdx(typeof parsed.page_index === 'number' ? parsed.page_index : 0);
            if (parsed.saved_at) setResumeBanner(`Welcome back — we restored your answers from ${relativeTime(parsed.saved_at)} (saved on this device).`);
          } else {
            setFormData(initial);
            setPageIdx(0);
          }
        } catch {
          setFormData(initial);
          setPageIdx(0);
        }
      } else if (res.has_draft && !res.requires_pin) {
        // Invite-based link with a server-side draft and no PIN gate — hydrate immediately.
        // When a PIN IS required, hydration happens inside handlePinContinue instead, since the
        // draft is PII and must stay behind the same server-side PIN check (Phase 0A invariant).
        try {
          const draftRes = await formIntakeApi.publicResume(token, {});
          setFormData(mergeDraft(initial, draftRes.draft_data));
          setPageIdx(draftRes.page_index ?? 0);
          if (draftRes.draft_updated_at) {
            setResumeBanner(`Welcome back — we restored your answers from ${relativeTime(draftRes.draft_updated_at)}.`);
          }
        } catch {
          // Resume failing (e.g. the draft vanished) shouldn't block opening the form fresh.
          setFormData(initial);
          setPageIdx(0);
        }
      } else {
        setFormData(initial);
        setPageIdx(0);
      }

      setPageState(res.requires_pin ? 'pin' : 'form');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404 || status === 410) {
        setErrorMsg('This link is invalid or has expired.');
      } else {
        setErrorMsg(getApiError(err, 'Failed to load form. Please try again.'));
      }
      setPageState('error');
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const [pinChecking, setPinChecking] = useState(false);

  const handlePinContinue = async () => {
    if (!pin.trim()) {
      setPinError('Please enter the access PIN.');
      return;
    }
    if (!token) return;
    setPinError('');
    setPinChecking(true);
    try {
      // PIN is verified server-side here (Phase 0A) — the schema itself was never behind the
      // gate (it's not PII), but this call is the gate for the PII-bearing draft (Phase 4).
      const draftRes = await formIntakeApi.publicResume(token, { access_pin: pin });
      if (schema) {
        const skeleton = buildSkeleton(schema);
        if (draftRes.draft_data) {
          setFormData(mergeDraft(skeleton, draftRes.draft_data));
          setPageIdx(draftRes.page_index ?? 0);
          if (draftRes.draft_updated_at) {
            setResumeBanner(`Welcome back — we restored your answers from ${relativeTime(draftRes.draft_updated_at)}.`);
          }
        } else {
          setFormData(skeleton);
        }
      }
      setPageState('form');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setPinError('Incorrect PIN. Please try again.');
      } else if (status === 409) {
        setPageState('already_submitted');
      } else if (status === 404 || status === 410) {
        setErrorMsg('This link is invalid or has expired.');
        setPageState('error');
      } else {
        setPinError(getApiError(err, 'Something went wrong. Please try again.'));
      }
    } finally {
      setPinChecking(false);
    }
  };

  const updateSectionField = (sectionKey: string, fieldId: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: { ...prev[sectionKey], [fieldId]: value },
    }));
  };

  const updateRepeatingSectionItems = (sectionKey: string, items: Record<string, any>[]) => {
    setFormData((prev) => ({ ...prev, [sectionKey]: items }));
  };

  // Chunk the flat sections array into pages on page_break_before (+ REPEATING_STARTS_PAGE).
  // Zero flags set anywhere ⇒ pages.length === 1 ⇒ pages[0] === schema.sections ⇒ byte-identical
  // to the pre-pagination single-page render. page_break_before on the FIRST section is ignored
  // (it can't start a page it already starts) — no leading empty page. Declared before `percent`
  // below, which now reads `pages`/`pageIdx` directly (see that block's comment for why).
  const pages = useMemo<FormIntakeSectionDef[][]>(() => {
    const sections = schema?.sections ?? [];
    const out: FormIntakeSectionDef[][] = [];
    sections.forEach((s, i) => {
      const startsNewPage = i > 0 && (s.page_break_before || (REPEATING_STARTS_PAGE && s.repeating));
      if (i === 0 || startsNewPage) out.push([]);
      out[out.length - 1].push(s);
    });
    return out.length ? out : [sections];
  }, [schema]);

  // Fraction (0-1) of ALL fields in `secs` that are filled. Deliberately NOT gated on
  // `field.required` — AI-extracted forms routinely mark nothing as required (the production
  // WILLS & ESTATE PLANNING QUESTIONNAIRE has 96 fields, 0 of them required), which used to make
  // the progress bar's denominator 0 on every section and fall through to a hardcoded "100%
  // complete" the instant the form opened. Counting every field is reliable regardless of how
  // (or whether) the schema's `required` flags are set.
  const fieldFillRatio = (secs: FormIntakeSectionDef[]): number => {
    let total = 0;
    let filled = 0;
    for (const section of secs) {
      if (section.repeating) {
        // Only the FIRST item counts — otherwise "+ Add another" would make the bar visibly go
        // backwards, the opposite of the gamification intent. The bar is motivational, not a
        // validation gate.
        const items: Record<string, any>[] = Array.isArray(formData[section.key])
          ? formData[section.key]
          : [{}];
        const first = items[0] ?? {};
        total += section.fields.length;
        filled += section.fields.filter((f) => isFilled(first[f.id])).length;
      } else {
        const vals = formData[section.key] ?? {};
        total += section.fields.length;
        filled += section.fields.filter((f) => isFilled(vals[f.id])).length;
      }
    }
    return total ? filled / total : 1; // no fields at all → nothing left to fill
  };

  // Continuous, live-updating progress — recomputes on every keystroke (formData dependency), not
  // only on page turns.
  //
  // Paginated forms (pages.length > 1): position in the form (which page you're on) is the
  // primary signal, with in-page field-fill blended in as a fraction of the current page so the
  // bar still moves while typing rather than only jumping on Next/Back. A form with N pages
  // reaches 100% only once the LAST page is fully filled — reaching page N alone caps at
  // (N-1)/N of the way there.
  //
  // Single-page forms (pages.length === 1): straight fraction of all fields filled.
  const percent = useMemo(() => {
    const sections = schema?.sections ?? [];
    if (!sections.length) return 100; // nothing to fill

    if (pages.length > 1) {
      const currentPageRatio = fieldFillRatio(pages[pageIdx] ?? []);
      const pageProgress = (pageIdx + currentPageRatio) / pages.length;
      return Math.round(PROGRESS_FLOOR + pageProgress * (100 - PROGRESS_FLOOR));
    }
    return Math.round(PROGRESS_FLOOR + fieldFillRatio(sections) * (100 - PROGRESS_FLOOR));
  }, [schema, formData, pages, pageIdx]);

  // Maps the ten style keys onto CSS custom properties on a scoping wrapper — same pattern as
  // widgets/src/components/QuizWidget.tsx's quizStyle object. Every key is conditionally spread,
  // so a null/undefined styleDef yields {} and every .pff-* rule falls through to its existing
  // var(--text)/var(--panel) second-level fallback — pixel-identical to before this feature.
  // Set via React's style prop ONLY — never dangerouslySetInnerHTML, never an injected <style>
  // tag (font_family is author-supplied text reaching a CSS value; React escapes inline style
  // values, so this is safe without needing site_components/css_scoper.py's CSS sanitisation).
  const pffStyle = useMemo<React.CSSProperties>(() => {
    const s = styleDef ?? {};
    return {
      ...(s.header_color ? { '--pff-header': s.header_color } : {}),
      ...(s.text_color ? { '--pff-text': s.text_color } : {}),
      ...(s.background_color ? { '--pff-bg': s.background_color } : {}),
      ...(s.page_background ?? s.background_color
        ? { '--pff-page-bg': s.page_background ?? s.background_color } : {}),
      ...(s.input_background ? { '--pff-input-bg': s.input_background } : {}),
      ...(s.input_text_color ? { '--pff-input-text': s.input_text_color } : {}),
      ...(s.border_color ? { '--pff-border': s.border_color } : {}),
      ...(s.button_color ? { '--pff-btn-bg': s.button_color } : {}),
      ...(s.button_text_color ? { '--pff-btn-text': s.button_text_color } : {}),
      ...(s.font_family ? { '--pff-font': `'${s.font_family}', -apple-system, sans-serif` } : {}),
    } as React.CSSProperties;
  }, [styleDef]);

  // Clamp pageIdx if the schema-derived page count ever shrinks below the current index
  // (e.g. a resumed draft's page_index is stale against an edited schema — Phase 4).
  useEffect(() => {
    setPageIdx((i) => Math.min(i, pages.length - 1));
  }, [pages.length]);

  const isLastPage = pageIdx === pages.length - 1;
  const currentPageSections = pages[pageIdx] ?? [];

  // Client-side required-field check for the CURRENT page only, before allowing Next. Submit
  // itself still sends the entire formData regardless of page — pagination never filters the
  // submit payload, it only gates forward navigation.
  const [pageValidationError, setPageValidationError] = useState('');
  const validateCurrentPage = (): boolean => {
    for (const section of currentPageSections) {
      const required = section.fields.filter((f) => f.required);
      if (!required.length) continue;
      if (section.repeating) {
        const items: Record<string, any>[] = Array.isArray(formData[section.key]) ? formData[section.key] : [{}];
        const first = items[0] ?? {};
        if (required.some((f) => !isFilled(first[f.id]))) {
          setPageValidationError('Please complete all required fields before continuing.');
          return false;
        }
      } else {
        const vals = formData[section.key] ?? {};
        if (required.some((f) => !isFilled(vals[f.id]))) {
          setPageValidationError('Please complete all required fields before continuing.');
          return false;
        }
      }
    }
    setPageValidationError('');
    return true;
  };

  // Same-device localStorage safety net for anonymous/open tokens — debounced 800ms, no network,
  // no identity. Only active once the form is actually being filled (pageState 'form'), so it
  // never fires while still on the PIN screen or before the schema has loaded.
  const localSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!isOpenToken || !token || pageState !== 'form') return;
    if (localSaveTimer.current) clearTimeout(localSaveTimer.current);
    localSaveTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(
          LOCAL_DRAFT_KEY(token),
          JSON.stringify({ data: formData, page_index: pageIdx, saved_at: new Date().toISOString() })
        );
      } catch {
        // localStorage can throw (quota, private-browsing restrictions) — same-device autosave
        // is a nice-to-have, never block the user's actual typing over it.
      }
    }, 800);
    return () => { if (localSaveTimer.current) clearTimeout(localSaveTimer.current); };
  }, [formData, pageIdx, isOpenToken, token, pageState]);

  // Server-side draft save for invite-based fillers — fired on page turn (auto) and by the
  // manual "Save Progress" button. No-op (returns silently) for anonymous/open tokens without an
  // email — that path is handled entirely client-side via the localStorage effect above.
  const saveServerDraft = async (explicitPageIndex?: number): Promise<boolean> => {
    if (!activeToken || isOpenToken) return false;
    setSaveStatus('saving');
    try {
      const res = await formIntakeApi.publicSaveDraft(activeToken, {
        data: formData,
        access_pin: requiresPin ? pin : undefined,
        page_index: explicitPageIndex ?? pageIdx,
      });
      setSaveStatus(res.saved ? 'saved' : 'local');
      return res.saved;
    } catch (err: any) {
      // A failed autosave must never block navigation — the user's in-memory formData is
      // unaffected, they just don't have a server checkpoint for this page yet.
      if (err?.response?.status === 410) {
        // The link expired mid-session. Unlike a transient failure, retrying can never succeed —
        // say so plainly instead of "try again", which would just have the visitor keep failing.
        setSaveStatus('expired');
      } else {
        setSaveStatus('error');
      }
      return false;
    }
  };

  const handleNextPage = () => {
    if (!validateCurrentPage()) return;
    const nextIdx = Math.min(pageIdx + 1, pages.length - 1);
    setPageIdx(nextIdx);
    window.scrollTo(0, 0);
    if (!isOpenToken && saveStatus !== 'expired') {
      // Fire-and-forget — do not await; a slow/failed draft save must never delay navigation.
      // Stop retrying once we know the link expired — every further call would 410 again, and
      // filling the log with "draft save on expired token" once per page turn is just noise.
      saveServerDraft(nextIdx);
    }
  };

  const handleBackPage = () => {
    // No draft write on Back — nothing changed by navigating backwards.
    setPageValidationError('');
    setPageIdx((i) => Math.max(i - 1, 0));
    window.scrollTo(0, 0);
  };

  const handleSaveProgress = async () => {
    if (!token) return;
    if (isOpenToken) {
      // Anonymous path: immediate local write (the debounced autosave effect may not have fired
      // yet), then offer the email upsell — cross-device resume via SaveForLaterModal. The button
      // never becomes a no-op: "Just save on this device" inside the modal keeps this local write
      // as the outcome, so a visitor who declines email still walks away with something saved.
      try {
        localStorage.setItem(
          LOCAL_DRAFT_KEY(token),
          JSON.stringify({ data: formData, page_index: pageIdx, saved_at: new Date().toISOString() })
        );
        setSaveStatus('local');
      } catch {
        setSaveStatus('error');
      }
      setShowSaveForLaterModal(true);
      return;
    }
    await saveServerDraft();
  };

  const handleSaveForLaterEmail = async (email: string): Promise<{ promoted: boolean; resume_path: string | null }> => {
    if (!token) throw new Error('missing token');
    const res = await formIntakeApi.publicSaveDraft(token, {
      data: formData,
      email,
      page_index: pageIdx,
    });
    if (res.promoted && res.resume_path) {
      const newToken = res.resume_path.replace(/^\/form-fill\//, '');
      // Swap to the new invite token WITHOUT a route navigation — every subsequent save/submit
      // call reads `activeToken`, which now resolves to this invite. isOpenToken flips false so
      // saveServerDraft's invite branch (not the localStorage branch) takes over from here.
      setResumeToken(newToken);
      setIsOpenToken(false);
      window.history.replaceState(null, '', res.resume_path);
      try {
        localStorage.setItem(RESUME_MAP_KEY(token), newToken);
      } catch {
        // Non-fatal — only affects the "revisit the original open link later" convenience path.
      }
    }
    return { promoted: res.promoted, resume_path: res.resume_path };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeToken) return;
    // Guard against an implicit Enter-key submit while on a non-last page — the Submit button
    // itself only renders on the last page, but a stray Enter keypress in a text field can
    // still fire the form's onSubmit in some browsers.
    if (!isLastPage) {
      handleNextPage();
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    try {
      // activeToken, not the raw URL token — if this session was promoted (Phase 5 save-for-
      // later), submission must land on the new invite token so it single-uses correctly.
      const res = await formIntakeApi.publicSubmit(activeToken, {
        data: formData,
        access_pin: requiresPin ? pin : undefined,
      });
      setSuccessMessage(res.message ?? 'Thank you! Your form has been submitted successfully.');
      // Capture booking CTA fields if backend returns them
      const resAny = res as any;
      if (resAny.booking_type_id) setBookingTypeId(resAny.booking_type_id);
      if (resAny.submission_id) setSubmissionId(resAny.submission_id);
      if (resAny.form_id) setFormId(resAny.form_id);
      setPageState('submitted');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        setSubmitError("You've already submitted this form.");
      } else if (status === 403) {
        setSubmitError('Incorrect PIN. Please try again.');
      } else if (status === 429) {
        setSubmitError('Too many attempts. Please try again later.');
      } else {
        setSubmitError(getApiError(err, 'Submission failed. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─ Render states ─

  if (pageState === 'loading') {
    return (
      <div className="pff-page pff-scope" style={pffStyle}>
        <div className="pff-card">
          <p className="pff-desc" style={{ textAlign: 'center' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="pff-page pff-scope" style={pffStyle}>
        <div className="pff-card">
          <h1 className="pff-title">Link Unavailable</h1>
          <p className="pff-desc">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (pageState === 'already_submitted') {
    return (
      <div className="pff-page pff-scope" style={pffStyle}>
        <div className="pff-card pff-success">
          <div className="pff-success-icon">
            <CheckCircle size={48} />
          </div>
          <h2 className="pff-success-title">Already Submitted</h2>
          <p className="pff-success-msg">You've already submitted this form. Thank you!</p>
        </div>
      </div>
    );
  }

  if (pageState === 'submitted') {
    // Build booking URL with optional query params
    let bookingUrl: string | null = null;
    if (bookingTypeId) {
      const params = new URLSearchParams();
      if (submissionId) params.set('s', submissionId);
      if (formId) params.set('f', formId);
      // Cheaply find name/email fields from submitted data
      const flat: Record<string, any> = {};
      Object.values(formData).forEach((section) => {
        if (section && typeof section === 'object' && !Array.isArray(section)) {
          Object.assign(flat, section);
        }
      });
      const nameVal = flat['name'] ?? flat['full_name'] ?? flat['first_name'] ?? '';
      const emailVal = flat['email'] ?? flat['email_address'] ?? '';
      if (nameVal) params.set('name', String(nameVal));
      if (emailVal) params.set('email', String(emailVal));
      bookingUrl = `/book/${bookingTypeId}?${params.toString()}`;
    }

    return (
      <div className="pff-page pff-scope" style={pffStyle}>
        <div className="pff-card pff-success">
          <div className="pff-success-icon">
            <CheckCircle size={48} />
          </div>
          <h2 className="pff-success-title">Submitted!</h2>
          <p className="pff-success-msg">{successMessage}</p>
          {bookingUrl && (
            <div className="pff-booking-cta">
              <p className="pff-booking-cta-title">One more step</p>
              <p className="pff-booking-cta-desc">Book your consultation to speak with our team.</p>
              <a href={bookingUrl} className="pff-booking-cta-btn">
                Book your consultation
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (pageState === 'pin') {
    return (
      <div className="pff-page pff-scope" style={pffStyle}>
        <div className="pff-card">
          <h1 className="pff-title">{formName}</h1>
          <div className="pff-pin-section">
            <label className="pff-pin-label" htmlFor="pff-pin">
              This form requires an access PIN.
            </label>
            <input
              id="pff-pin"
              className="pff-input"
              type="password"
              placeholder="Enter PIN"
              value={pin}
              disabled={pinChecking}
              onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePinContinue()}
            />
            {pinError && <p className="pff-error">{pinError}</p>}
          </div>
          <button
            type="button"
            className="pff-submit-btn"
            onClick={handlePinContinue}
            disabled={pinChecking}
          >
            {pinChecking ? 'Checking…' : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  // pageState === 'form'
  if (!schema) return null;

  const pageLabel = pages.length > 1 ? `Page ${pageIdx + 1} of ${pages.length}` : undefined;

  return (
    <div className="pff-page pff-scope" style={pffStyle}>
      <div className="pff-card">
        <h1 className="pff-title">{formName}</h1>
        <p className="pff-desc">Please fill in all required fields and submit the form below.</p>
        {resumeBanner && <p className="pff-resume-banner">{resumeBanner}</p>}
        <FormProgressBar percent={percent} pageLabel={pageLabel} />

        <form onSubmit={handleSubmit} noValidate>
          {currentPageSections.map((section) => {
            if (section.repeating) {
              const items: Record<string, any>[] = Array.isArray(formData[section.key])
                ? formData[section.key]
                : [{}];
              return (
                <RepeatingSection
                  key={section.key}
                  section={section}
                  items={items}
                  onChange={(newItems) => updateRepeatingSectionItems(section.key, newItems)}
                />
              );
            }

            return (
              <div key={section.key} className="pff-section">
                <h3 className="pff-section-title">{section.title}</h3>
                {section.description && <p className="fi-help">{section.description}</p>}
                {section.fields.map((field) => (
                  <FieldInput
                    key={field.id}
                    field={field}
                    value={(formData[section.key] ?? {})[field.id]}
                    onChange={(v) => updateSectionField(section.key, field.id, v)}
                  />
                ))}
              </div>
            );
          })}

          {pageValidationError && <p className="pff-error">{pageValidationError}</p>}
          {submitError && <p className="pff-error">{submitError}</p>}

          <div className="pff-pager">
            {pageIdx > 0 && (
              <button type="button" className="pff-nav-btn" onClick={handleBackPage} disabled={submitting}>
                Back
              </button>
            )}
            {!isLastPage ? (
              <button type="button" className="pff-nav-btn pff-nav-btn-primary" onClick={handleNextPage}>
                Next
              </button>
            ) : (
              <button type="submit" className="pff-submit-btn" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            )}
          </div>

          <div className="pff-save-progress">
            <button type="button" className="pff-save-progress-btn" onClick={handleSaveProgress} disabled={saveStatus === 'saving' || saveStatus === 'expired'}>
              {saveStatus === 'saving' ? 'Saving…' : 'Save Progress'}
            </button>
            {saveStatus === 'saved' && <span className="pff-save-status pff-save-status-ok">Saved</span>}
            {saveStatus === 'local' && <span className="pff-save-status">Saved on this device</span>}
            {saveStatus === 'error' && <span className="pff-save-status pff-save-status-error">Couldn't save — your answers are still here, try again</span>}
            {saveStatus === 'expired' && <span className="pff-save-status pff-save-status-error">This link has expired — contact the form owner for a new one</span>}
          </div>
        </form>
      </div>
      {showSaveForLaterModal && (
        <SaveForLaterModal
          onClose={() => setShowSaveForLaterModal(false)}
          onSaveLocalOnly={() => setShowSaveForLaterModal(false)}
          onEmailSubmit={handleSaveForLaterEmail}
        />
      )}
    </div>
  );
};

export default PublicFormFill;
